import React, { useContext, useEffect } from "react";
import Form from "@rjsf/core";
import { RegistryFieldsType, RegistryWidgetsType } from "@rjsf/utils";
import { useFormContext } from "react-hook-form";
import { decorateAndGenerateFormStructure } from "common/services/formBuilderService";
import { arrayHasAtLeastOneItem } from "common/utils/arrays";
import { formatDateString } from "common/utils/dates";
import { spacing } from "../../../../../../stitches.config";
import { Header } from "../../../../../Common/__styles__/FullPageFormLayout";
import { EmptyState } from "../../../../../Common/EmptyState";
import { IllustrationName } from "../../../../../Common/Illustration";
import { Title } from "../../../../../Common/Typography";
import { DescriptionContainer } from "../../../../../Submissions/__styles__/CreateSubmission";
import { SubmissionFormContext } from "../../../../../Submissions/Form";
import {
  DescriptionFieldTemplate,
  TitleFieldTemplate,
} from "../../../../../Submissions/Templates";
import { ColumnWrapper } from "../__styles__/SubmissionsBuilder";
import { SubmissionsBuilderContextInstance } from "../context";
import {
  FieldContext,
  SubmissionsBuilderField,
  SubmissionsBuilderFormDataStructure,
} from "../types";
import { buildFieldContextsByNameMap } from "../utils";
import CheckboxWidgetPreview from "./FieldPreviews/CheckboxWidgetPreview";
import DamageDegreeWidgetPreview from "./FieldPreviews/DamageDegreeWidgetPreview";
import DateWidgetPreview from "./FieldPreviews/DateWidgetPreview";
import DocumentUploadFieldPreview from "./FieldPreviews/DocumentUploadFieldPreview";
import MultiSelectWidgetPreview from "./FieldPreviews/MultiSelectFieldPreview";
import NumberFieldPreview from "./FieldPreviews/NumberFieldPreview";
import NumberWithUnitsFieldPreview from "./FieldPreviews/NumberWithUnitsFieldPreview";
import ObjectFieldTemplatePreview from "./FieldPreviews/ObjectFieldTemplatePreview";
import PropertyMarketValueFieldPreview from "./FieldPreviews/PropertyMarketValueFieldPreview";
import SelectWidgetPreview from "./FieldPreviews/SelectWidgetPreview";
import TagFieldPreview from "./FieldPreviews/TagFieldPreview";
import TextareaWidgetPreview from "./FieldPreviews/TextareaWidgetPreview";
import TextWidgetPreview from "./FieldPreviews/TextWidgetPreview";
import UniqueIdFieldPreview from "./FieldPreviews/UniqueIdFieldPreview";
import UserFieldPreview from "./FieldPreviews/UserFieldPreview";
import useFieldActionsPropsUpdater from "./useFieldActionsPropsUpdater";

const builderWidgets: RegistryWidgetsType = {
  TextWidget: TextWidgetPreview,
  TextareaWidget: TextareaWidgetPreview,
  DateWidget: DateWidgetPreview,
  CheckboxWidget: CheckboxWidgetPreview,
  DamageDegree: DamageDegreeWidgetPreview,
  SelectWidget: SelectWidgetPreview,
};

const builderFields: RegistryFieldsType = {
  multiSelect: MultiSelectWidgetPreview,
  NumberField: NumberFieldPreview,
  NumberWithUnits: NumberWithUnitsFieldPreview,
  PropertyMarketValue: PropertyMarketValueFieldPreview,
  DocumentUploader: DocumentUploadFieldPreview,
  Tags: TagFieldPreview,
  User: UserFieldPreview,
  UniqueId: UniqueIdFieldPreview,
};

const builderTemplates = {
  TitleFieldTemplate: TitleFieldTemplate,
  DescriptionFieldTemplate: DescriptionFieldTemplate,
  ObjectFieldTemplate: ObjectFieldTemplatePreview,
};

const EmptyForm = ({
  icon,
  title,
  body,
}: {
  icon: IllustrationName;
  title: string;
  body: string;
}) => {
  const formMethods = useFormContext<SubmissionsBuilderFormDataStructure>();
  useFieldActionsPropsUpdater(formMethods, "inputs");

  return (
    <div style={{ marginTop: "128px" }}>
      <EmptyState icon={icon} title={title} body={body} />
    </div>
  );
};

const FormWrapper = ({
  formName,
  inputs,
}: {
  formName: string;
  inputs: SubmissionsBuilderField[];
}) => {
  useFieldPathBuilder(formName, inputs);

  let schema,
    uiSchema = {};

  try {
    ({ schema, uiSchema } = decorateAndGenerateFormStructure({
      formData: inputs,
    }));
  } catch (e) {
    return (
      <div style={{ marginTop: "128px" }}>
        <EmptyState
          icon="error-alert"
          title="Error generating form"
          body="Review the Form Structure JSON or contact support."
        />
      </div>
    );
  }

  return (
    <Form
      fields={builderFields}
      formData={{}}
      formContext={{}}
      noHtml5Validate={true}
      onChange={() => {}}
      onError={() => {}}
      onSubmit={() => {}}
      schema={schema}
      showErrorList={false}
      templates={builderTemplates}
      uiSchema={{
        ...uiSchema,
        // This is a hack to prevent the submit button from rendering
        "ui:submitButtonOptions": { norender: true },
      }}
      widgets={builderWidgets}
      validator={{
        isValid: () => true,
        toErrorList: () => [],
        rawValidation: () => ({}),
        validateFormData: () => ({
          errors: [],
          errorSchema: {},
        }),
      }}
    />
  );
};

const useFieldPathBuilder = (
  formName: string,
  formData: SubmissionsBuilderField[],
) => {
  const { updateFieldContextsByNameMap } = useContext(
    SubmissionsBuilderContextInstance,
  );

  useEffect(() => {
    const fieldPathsByNames: Record<string, FieldContext> =
      buildFieldContextsByNameMap(formName, formData);
    updateFieldContextsByNameMap(fieldPathsByNames);
  }, [JSON.stringify(formData), updateFieldContextsByNameMap]);
};

export const MainColumn = () => {
  const { watch } = useFormContext<SubmissionsBuilderFormDataStructure>();

  const { inputs, name, description } = watch();

  return (
    <SubmissionFormContext.Provider
      value={{
        editing: false,
        documentUploads: null,
        defaultValues: {
          units: {
            feet: "feet",
            inches: "inches",
            hours: "hours",
            minutes: "minutes",
            days: "days",
          } as const,
          boolean: { true: true, false: false } as const,
          date: {
            today: formatDateString({
              dateString: new Date().toDateString(),
              format: "YYYY-MM-DD",
            }),
          },
          property: {
            improvementValue: 1000,
            sisd: {
              adjustmentRatio: 0.5,
            },
          },
        },
        regenerateAIFields: null,
        loadingFormPaths: {},
      }}
    >
      <ColumnWrapper role="main">
        <div style={{ maxWidth: "564px", width: "100%", margin: "0 auto" }}>
          <Header css={{ marginBottom: spacing.l }}>
            <Title
              style={{ marginLeft: spacing.m.toString() }}
              size="default"
              type="semiBold"
            >
              {name}
            </Title>
            {description && <DescriptionContainer markdown={description} />}
          </Header>
          {arrayHasAtLeastOneItem(inputs) ?
            <FormWrapper formName={name} inputs={inputs} />
          : <EmptyForm
              icon="add-file"
              title="No fields added to your form"
              body="Add a field to get started."
            />
          }
        </div>
      </ColumnWrapper>
    </SubmissionFormContext.Provider>
  );
};
